<script>
import { useAlert } from 'dashboard/composables';
import { mapGetters } from 'vuex';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import AddCannedModal from 'dashboard/routes/dashboard/settings/canned/AddCanned.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { conversationUrl, frontendURL } from '../../../helper/URLHelper';
import {
  ACCOUNT_EVENTS,
  CONVERSATION_EVENTS,
} from '../../../helper/AnalyticsHelper/events';
import MenuItem from '../../../components/widgets/conversation/contextMenu/menuItem.vue';
import { useTrack } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import AudioPresentation from 'dashboard/components-next/message/bubbles/AudioPresentation.vue';
import {
  getAudioAppearance,
  getAudioAvatarIdentity,
  isForwardedAudio,
} from 'dashboard/components-next/message/bubbles/audioIdentity';
import FileIcon from 'dashboard/components-next/icon/FileIcon.vue';
import { createPdfFirstPagePreviewFromUrl } from 'dashboard/helper/pdfPreviewHelper';
import MessageReactionBar from '../../../components/widgets/conversation/MessageReactionBar.vue';
import WhatsAppEmojiPicker from '../../../components/widgets/conversation/WhatsAppEmojiPicker.vue';
import AceleresMessageApi from 'dashboard/api/aceleres/messageActions';
import instagramClient from 'dashboard/api/channel/instagramClient';
import ForwardMessageModal from './ForwardMessageModal.vue';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { provide, ref } from 'vue';
import { useMessageSelection } from 'dashboard/composables/useMessageSelection.js';

function getDashboardZoom() {
  const zoom = parseFloat(getComputedStyle(document.documentElement).zoom);
  return Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
}

function fixedPosition(value, zoom = getDashboardZoom()) {
  return `${value / zoom}px`;
}

function getVisualViewportBounds() {
  const zoom = getDashboardZoom();
  const rootRect = document.documentElement.getBoundingClientRect();

  return {
    zoom,
    left: rootRect.left,
    top: rootRect.top,
    width: rootRect.width || window.innerWidth * zoom,
    height: rootRect.height || window.innerHeight * zoom,
  };
}

function getUntransformedPopupSize(root, rect) {
  const transform = getComputedStyle(root).transform;
  const matrix = transform.match(/^matrix\(([^)]+)\)$/);
  const values = matrix?.[1]?.split(',').map(Number);
  const scaleX = Math.abs(values?.[0]) || 1;
  const scaleY = Math.abs(values?.[3]) || 1;

  return {
    width: rect.width / scaleX,
    height: rect.height / scaleY,
  };
}

function decodeAttachmentName(value) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

// Aceleres: Attachment#file_metadata nao envia file_name nem fallback_title,
// entao o nome real do documento vem do ultimo segmento da data_url do
// ActiveStorage — mesma estrategia do bubble do chat (bubbles/File.vue).
function attachmentNameFromUrl(url) {
  const cleanUrl = String(url || '').split(/[?#]/, 1)[0];
  const name = cleanUrl.substring(cleanUrl.lastIndexOf('/') + 1);
  return decodeAttachmentName(name);
}

function formatAttachmentSize(bytes) {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size <= 0) return '';

  const units = ['B', 'KB', 'MB', 'GB'];
  const unitIndex = Math.min(
    Math.floor(Math.log(size) / Math.log(1024)),
    units.length - 1
  );
  const value = size / 1024 ** unitIndex;
  const rounded =
    unitIndex === 0 || value >= 10 ? Math.round(value) : value.toFixed(1);

  return `${rounded} ${units[unitIndex]}`;
}

export default {
  components: {
    AddCannedModal,
    MenuItem,
    ContextMenu,
    NextButton,
    AudioPresentation,
    FileIcon,
    MessageReactionBar,
    WhatsAppEmojiPicker,
    ForwardMessageModal,
  },
  props: {
    message: {
      type: Object,
      required: true,
    },
    // Aceleres: um álbum de imagens chega como uma única bolha visual, mas
    // continua sendo composto por várias mensagens reais. As ações do menu
    // devem receber todas elas para não operar apenas sobre a última.
    selectionMessages: {
      type: Array,
      default: () => [],
    },
    isOpen: {
      type: Boolean,
      default: false,
    },
    enabledOptions: {
      type: Object,
      default: () => ({}),
    },
    contextMenuPosition: {
      type: Object,
      default: () => ({}),
    },
    hideButton: {
      type: Boolean,
      default: false,
    },
    currentUserId: {
      type: Number,
      default: null,
    },
    // Aceleres: quando true, ao abrir o popup já entra direto no picker full
    // (usado pelo "+" da hover-pill de reação rápida em Message.vue).
    initialShowPicker: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['open', 'close', 'replyTo', 'react'],
  setup() {
    // Aceleres: sobrescreve o `contextMenuElementTarget` provido pelo
    // MessagesView para que o useScrollLock do shared ContextMenu vire
    // no-op — senão o scroll do painel inteiro some e o conteudo shifta
    // de largura quando o menu abre.
    provide('contextMenuElementTarget', ref(null));
    const { getPlainText } = useMessageFormatter();
    const {
      isSelectionMode,
      enterSelectionMode,
      exitSelectionMode,
      isMessageSelected,
      toggleMessageSelection,
    } = useMessageSelection();
    return {
      getPlainText,
      isSelectionMode,
      enterSelectionMode,
      exitSelectionMode,
      isMessageSelected,
      toggleMessageSelection,
    };
  },
  data() {
    return {
      isCannedResponseModalOpen: false,
      showDeleteModal: false,
      deleteSubmitting: false,
      showEditModal: false,
      editAnchorRect: null,
      editPopupStyle: null,
      editContent: '',
      editOriginalContent: '',
      editSubmitting: false,
      showEditEmojiPicker: false,
      editEmojiPickerStyle: null,
      showForwardModal: false,
      showEmojiPicker: false,
      showMessageInfoModal: false,
      messageInfoPdfPreviews: {},
      messageInfoPdfLoading: {},
      resolvedContextMenuPosition: {},
      aceleresOutsideHandler: null,
      aceleresScrollHandler: null,
      aceleresKeyHandler: null,
      aceleresViewportHandler: null,
      editOutsideHandler: null,
      editScrollHandler: null,
      editResizeHandler: null,
    };
  },
  watch: {
    contextMenuPosition: {
      deep: true,
      immediate: true,
      handler(position) {
        this.resolvedContextMenuPosition = { ...(position || {}) };
        if (this.isOpen) this.$nextTick(this.clampPopupToViewport);
      },
    },
    isOpen(open) {
      if (open) {
        this.showEditModal = false;
        this.editSubmitting = false;
        this.closeEditEmojiPicker();
        // Toda abertura do popup reseta estados internos — se o picker
        // grande estava aberto na última vez, esquece e volta ao menu normal.
        // EXCEÇÃO: se `initialShowPicker` veio true (chamado pelo "+" da
        // hover-pill em Message.vue), entra direto no picker.
        this.showEmojiPicker = !!this.initialShowPicker;
        if (this.showEmojiPicker) {
          emitter.emit(BUS_EVENTS.EMOJI_PICKER_OPENED, {
            owner: 'message',
            messageId: this.messageId,
          });
        }
        this.resolvedContextMenuPosition = {
          ...(this.contextMenuPosition || {}),
        };
        // Click-outside fecha o popup. setTimeout pra não pegar o próprio
        // clique do chevron que abriu (que ainda está propagando).
        setTimeout(() => {
          if (!this.isOpen) return;
          this.aceleresOutsideHandler = e => {
            const root = this.$refs.aceleresPopupRoot;
            if (root && !root.contains(e.target)) {
              this.handleClose();
            }
          };
          document.addEventListener(
            'pointerdown',
            this.aceleresOutsideHandler,
            true
          );
        }, 0);
        // Scroll em QUALQUER ancestor (capture:true pega o painel da
        // conversa) fecha — popup é position:fixed, se a mensagem rola,
        // o popup ficaria pendurado no ar; melhor fechar.
        // EXCEÇÃO: scroll DENTRO do próprio popup (ex: rolar a lista de
        // emojis no picker) NÃO fecha — verifica se o target tá contido.
        this.aceleresScrollHandler = e => {
          const root = this.$refs.aceleresPopupRoot;
          if (root && e.target && root.contains(e.target)) return;
          this.handleClose();
        };
        window.addEventListener('scroll', this.aceleresScrollHandler, {
          capture: true,
          passive: true,
        });
        this.aceleresKeyHandler = event => {
          if (event.key === 'Escape') this.handleClose();
        };
        document.addEventListener('keydown', this.aceleresKeyHandler);
        this.aceleresViewportHandler = () => this.handleClose();
        window.addEventListener('resize', this.aceleresViewportHandler, {
          passive: true,
        });
        window.visualViewport?.addEventListener(
          'resize',
          this.aceleresViewportHandler,
          { passive: true }
        );
        window.visualViewport?.addEventListener(
          'scroll',
          this.aceleresViewportHandler,
          { passive: true }
        );
        this.$nextTick(() => {
          this.clampPopupToViewport();
          this.focusPopupForKeyboard();
        });
      } else {
        if (this.aceleresOutsideHandler) {
          document.removeEventListener(
            'pointerdown',
            this.aceleresOutsideHandler,
            true
          );
          this.aceleresOutsideHandler = null;
        }
        if (this.aceleresScrollHandler) {
          window.removeEventListener(
            'scroll',
            this.aceleresScrollHandler,
            true
          );
          this.aceleresScrollHandler = null;
        }
        if (this.aceleresKeyHandler) {
          document.removeEventListener('keydown', this.aceleresKeyHandler);
          this.aceleresKeyHandler = null;
        }
        if (this.aceleresViewportHandler) {
          window.removeEventListener('resize', this.aceleresViewportHandler);
          window.visualViewport?.removeEventListener(
            'resize',
            this.aceleresViewportHandler
          );
          window.visualViewport?.removeEventListener(
            'scroll',
            this.aceleresViewportHandler
          );
          this.aceleresViewportHandler = null;
        }
        this.showEditModal = false;
        this.editSubmitting = false;
        this.closeEditEmojiPicker();
      }
    },
    showEditModal(open) {
      if (open) {
        this.$nextTick(() => {
          const ta = this.$refs.aceleresEditTextarea;
          if (ta && typeof ta.focus === 'function') {
            ta.focus();
            try {
              ta.setSelectionRange(
                this.editContent.length,
                this.editContent.length
              );
            } catch (_) {
              /* ignore */
            }
          }
        });
      } else {
        this.closeEditEmojiPicker();
      }
    },
    showEmojiPicker() {
      this.$nextTick(this.clampPopupToViewport);
    },
    initialShowPicker(showPicker) {
      if (!this.isOpen) return;
      this.showEmojiPicker = showPicker;
      if (showPicker) {
        emitter.emit(BUS_EVENTS.EMOJI_PICKER_OPENED, {
          owner: 'message',
          messageId: this.messageId,
        });
      }
    },
  },
  mounted() {
    emitter.on(BUS_EVENTS.EMOJI_PICKER_OPENED, this.onEmojiPickerOpened);
  },
  beforeUnmount() {
    if (this.aceleresOutsideHandler) {
      document.removeEventListener(
        'pointerdown',
        this.aceleresOutsideHandler,
        true
      );
    }
    if (this.aceleresScrollHandler) {
      window.removeEventListener('scroll', this.aceleresScrollHandler, true);
    }
    if (this.aceleresKeyHandler) {
      document.removeEventListener('keydown', this.aceleresKeyHandler);
    }
    if (this.aceleresViewportHandler) {
      window.removeEventListener('resize', this.aceleresViewportHandler);
      window.visualViewport?.removeEventListener(
        'resize',
        this.aceleresViewportHandler
      );
      window.visualViewport?.removeEventListener(
        'scroll',
        this.aceleresViewportHandler
      );
    }
    document.removeEventListener('keydown', this.handleMessageInfoEscape);
    // Aceleres: garante que o listener em capture do picker de emoji do modal
    // de editar não fique pendurado se o componente sair fora do fluxo normal.
    document.removeEventListener(
      'mousedown',
      this.handleEditEmojiOutsideClick,
      true
    );
    emitter.off(BUS_EVENTS.EMOJI_PICKER_OPENED, this.onEmojiPickerOpened);
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      currentAccountId: 'getCurrentAccountId',
      getUISettings: 'getUISettings',
    }),
    plainTextContent() {
      return this.getPlainText(this.messageContent);
    },
    // Moderação de comentário do Instagram: só existe em mensagem que veio de
    // um comentário, identificada pelo bloco gravado no content_attributes.
    instagramComment() {
      const attributes =
        this.message.content_attributes || this.message.contentAttributes || {};
      return (
        attributes.instagram_comment || attributes.instagramComment || null
      );
    },
    canModerateComment() {
      return (
        Boolean(this.instagramComment?.comment_id) &&
        !this.instagramComment?.deleted
      );
    },
    isCommentHidden() {
      return Boolean(this.instagramComment?.hidden);
    },
    conversationId() {
      return this.message.conversation_id ?? this.message.conversationId;
    },
    messageId() {
      return this.message.id;
    },
    actionMessages() {
      return this.selectionMessages.length
        ? this.selectionMessages
        : [this.message];
    },
    isMessageGroup() {
      return this.actionMessages.length > 1;
    },
    messageContent() {
      return this.message.content;
    },
    contentAttributes() {
      return useSnakeCase(
        this.message.content_attributes ?? this.message.contentAttributes
      );
    },
    isPinned() {
      return !!(this.enabledOptions && this.enabledOptions.pinned);
    },
    isStarred() {
      const starred = this.getUISettings?.starred_message_ids || [];
      return starred.some(id => Number(id) === Number(this.messageId));
    },
    isFailedMessage() {
      return this.message.status === 'failed';
    },
    popupAriaLabel() {
      return this.showEmojiPicker ? 'Escolher reação' : 'Ações da mensagem';
    },
    hasEditChanged() {
      // Aceleres: desabilita "Salvar" enquanto o usuário nao alterar o texto
      // ou digitar algo (evita PATCH sem mudanca).
      const now = (this.editContent || '').trim();
      return now.length > 0 && now !== (this.editOriginalContent || '').trim();
    },
    // Aceleres: dados pro modal "Info da mensagem" (timestamps + status).
    messageInfo() {
      const fmt = ts => {
        if (!ts) return null;
        const d = new Date(typeof ts === 'number' ? ts * 1000 : ts);
        if (Number.isNaN(d.getTime())) return null;
        return d.toLocaleString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      };
      const attrs = this.contentAttributes || {};
      const status = String(this.message.status || '').toLowerCase();
      const messageType = this.message.message_type ?? this.message.messageType;
      const isIncoming = messageType === 0 || messageType === 'incoming';
      const readReceipts = Array.isArray(attrs.read_receipts)
        ? attrs.read_receipts
            .filter(receipt => receipt && receipt.participant_jid)
            .map(receipt => ({
              participantJid: receipt.participant_jid,
              name: receipt.name,
              avatarUrl: receipt.avatar_url,
              readAt: fmt(receipt.read_at),
            }))
        : [];
      const deliveredAt =
        fmt(attrs.delivered_at) || fmt(attrs.external_delivered_at);
      const readAt = fmt(attrs.read_at) || fmt(attrs.external_read_at);
      const sentAt =
        fmt(this.message.created_at) ||
        fmt(attrs.created_at) ||
        fmt(attrs.external_created_at);
      // A cópia que chega ao atendimento já foi entregue ao Zoraxy por
      // definição. A leitura é confirmada somente depois de o atendente abrir
      // a conversa e a WAME aceitar o markRead, cujo horário vem em read_at.
      const delivered =
        isIncoming || ['delivered', 'read'].includes(status) || !!deliveredAt;
      const read = isIncoming
        ? !!readAt
        : status === 'read' || !!readAt || readReceipts.length > 0;
      return {
        sentAt,
        deliveredAt: isIncoming ? sentAt : deliveredAt,
        deliveredDisplay:
          (isIncoming ? sentAt : deliveredAt) ||
          (delivered
            ? 'Entregue (horário indisponível)'
            : 'Ainda não entregue'),
        readAt,
        readDisplay: isIncoming
          ? readAt || 'Ainda não lida no atendimento'
          : readAt || (read ? 'Lida (horário indisponível)' : 'Ainda não lida'),
        hasDelivered: delivered,
        hasRead: read,
        labels: {
          sent: isIncoming ? 'Recebida' : 'Enviada',
          delivered: isIncoming ? 'Recebida no atendimento' : 'Entregue',
          read: isIncoming ? 'Lida no atendimento' : 'Lida',
          readBy: isIncoming ? 'Lida no atendimento' : 'Lido por',
          emptyContent: 'Mensagem sem conteúdo visível',
        },
        isIncoming,
        isGroup:
          attrs.is_group === true ||
          String(attrs.remote_jid || attrs.wame_remote_jid || '').endsWith(
            '@g.us'
          ) ||
          readReceipts.length > 0,
        readReceipts,
      };
    },
    messageInfoAttachments() {
      const attachments = Array.isArray(this.message.attachments)
        ? this.message.attachments
        : [];

      return attachments.map((attachment, index) => {
        const rawType = String(
          attachment.file_type ||
            attachment.fileType ||
            attachment.content_type ||
            attachment.contentType ||
            attachment.mime_type ||
            attachment.mimeType ||
            ''
        ).toLowerCase();
        const type = rawType.includes('/') ? rawType.split('/')[0] : rawType;
        const url =
          attachment.data_url ||
          attachment.dataUrl ||
          attachment.thumb_url ||
          attachment.thumbUrl ||
          '';
        const mimeType =
          attachment.content_type ||
          attachment.contentType ||
          attachment.mime_type ||
          attachment.mimeType ||
          '';
        const metadataName =
          attachment.file_name ||
          attachment.fileName ||
          attachment.fallback_title ||
          attachment.fallbackTitle ||
          '';
        const name =
          (metadataName
            ? decodeAttachmentName(metadataName)
            : attachmentNameFromUrl(url)) || 'Anexo';
        const metadataExtension =
          attachment.extension || attachment.fileExtension || '';
        const extension = String(
          metadataExtension || name.match(/\.([a-z0-9]+)$/i)?.[1] || ''
        )
          .replace('.', '')
          .toLowerCase();

        return {
          key: attachment.id || `${this.messageId}-${index}`,
          url,
          type,
          mimeType,
          name,
          extension,
          typeLabel:
            extension.toUpperCase() ||
            this.$t('CONVERSATION.UNKNOWN_FILE_TYPE'),
          sizeLabel: formatAttachmentSize(
            attachment.file_size ?? attachment.fileSize
          ),
          isPdf:
            mimeType.toLowerCase() === 'application/pdf' || extension === 'pdf',
        };
      });
    },
    messageInfoHasAudio() {
      return this.messageInfoAttachments.some(
        attachment => attachment.type === 'audio' && attachment.url
      );
    },
    messageInfoConversation() {
      const getConversationById = this.$store.getters.getConversationById;

      if (typeof getConversationById !== 'function') return {};
      return getConversationById(this.conversationId) || {};
    },
    messageInfoInbox() {
      const inboxId =
        this.message.inbox_id ??
        this.message.inboxId ??
        this.messageInfoConversation.inbox_id ??
        this.messageInfoConversation.inboxId;
      const getInbox = this.$store.getters['inboxes/getInbox'];

      if (!inboxId || typeof getInbox !== 'function') return {};
      return getInbox(inboxId) || {};
    },
    messageInfoAudioSender() {
      const sender = this.message.sender || {
        name: this.message.sender_name || this.message.senderName || '',
      };

      return getAudioAvatarIdentity({
        contentAttributes: this.contentAttributes,
        sender,
        inbox: this.messageInfoInbox,
        conversation: this.messageInfoConversation,
        isOutgoing: this.isOutgoingMessageInfo,
      });
    },
    messageInfoAudioForwarded() {
      return isForwardedAudio(this.contentAttributes);
    },
    messageInfoAudioAppearance() {
      return getAudioAppearance({
        contentAttributes: this.contentAttributes,
        isOutgoing: this.isOutgoingMessageInfo,
      });
    },
    deleteConfirmationText() {
      if (this.isMessageGroup) {
        return `Serão excluídas ${this.actionMessages.length} imagens deste álbum.`;
      }

      return this.$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.MESSAGE');
    },
    isOutgoingMessageInfo() {
      const type = this.message.message_type ?? this.message.messageType;
      return type === 1 || type === 'outgoing';
    },
    editMenuLabel() {
      return this.isFailedMessage ? 'Editar e reenviar' : 'Editar mensagem';
    },
    editModalTitle() {
      return this.isFailedMessage ? 'Editar e reenviar' : 'Editar mensagem';
    },
    editModalHint() {
      return this.isFailedMessage
        ? 'A mensagem falhou no envio. Ao salvar, ela sera atualizada e reenviada.'
        : 'A edição só funciona em mensagens WhatsApp enviadas há menos de 15 min. Ctrl + Enter para salvar.';
    },
    editSubmitLabel() {
      if (this.editSubmitting) {
        return this.isFailedMessage ? 'Reenviando…' : 'Enviando…';
      }
      return this.isFailedMessage ? 'Salvar e reenviar' : 'Salvar';
    },
  },
  methods: {
    // hide/unhide/delete do comentário na própria publicação do Instagram.
    async moderateInstagramComment(action) {
      try {
        const { data } = await instagramClient.moderateComment(
          this.messageId,
          action
        );
        // Passa pela store em vez de mutar a prop: é a store que alimenta
        // todas as bolhas da conversa, então o novo estado sobrevive a
        // reabrir o menu.
        this.$store.dispatch('updateMessage', {
          ...this.message,
          content_attributes: {
            ...(this.message.content_attributes || {}),
            instagram_comment: data.payload,
          },
        });
        useAlert(
          action === 'delete'
            ? 'Comentário excluído no Instagram.'
            : 'Comentário atualizado no Instagram.'
        );
      } catch (error) {
        useAlert(
          error?.response?.data?.error ||
            'O Instagram recusou a moderação deste comentário.'
        );
      } finally {
        this.$emit('close');
      }
    },
    async copyLinkToMessage() {
      const fullConversationURL =
        window.chatwootConfig.hostURL +
        frontendURL(
          conversationUrl({
            id: this.conversationId,
            accountId: this.currentAccountId,
          })
        );
      await copyTextToClipboard(
        `${fullConversationURL}?messageId=${this.messageId}`
      );
      useAlert(this.$t('CONVERSATION.CONTEXT_MENU.LINK_COPIED'));
      this.handleClose();
    },
    async handleCopy() {
      await copyTextToClipboard(this.plainTextContent);
      useAlert(this.$t('CONTACT_PANEL.COPY_SUCCESSFUL'));
      this.handleClose();
    },
    showCannedResponseModal() {
      useTrack(ACCOUNT_EVENTS.ADDED_TO_CANNED_RESPONSE);
      this.isCannedResponseModalOpen = true;
    },
    hideCannedResponseModal() {
      this.isCannedResponseModalOpen = false;
      this.handleClose();
    },
    handleOpen(e) {
      this.$emit('open', e);
    },
    handleClose(e) {
      this.$emit('close', e);
    },
    clampPopupToViewport() {
      const root = this.$refs.aceleresPopupRoot;
      if (!root?.getBoundingClientRect) return;

      const rect = root.getBoundingClientRect();
      const {
        zoom,
        left: viewportLeft,
        top: viewportTop,
        width: viewportWidth,
        height: viewportHeight,
      } = getVisualViewportBounds();
      const popupSize = getUntransformedPopupSize(root, rect);
      const popupWidth = popupSize.width;
      const popupHeight = popupSize.height;
      const padding = 8;
      const minLeft = viewportLeft + padding;
      const minTop = viewportTop + padding;
      const maxRight = viewportLeft + viewportWidth - padding;
      const maxBottom = viewportTop + viewportHeight - padding;
      const anchorLeft = Number(this.resolvedContextMenuPosition.anchorLeft);
      const anchorTop = Number(this.resolvedContextMenuPosition.anchorTop);
      const anchorRight = Number(this.resolvedContextMenuPosition.anchorRight);
      const anchorBottom = Number(
        this.resolvedContextMenuPosition.anchorBottom
      );
      const anchorWidth = Number(this.resolvedContextMenuPosition.anchorWidth);
      const anchorHeight = Number(
        this.resolvedContextMenuPosition.anchorHeight
      );
      const anchorCenter = Number(
        this.resolvedContextMenuPosition.anchorCenter
      );

      if (Number.isFinite(anchorLeft) && Number.isFinite(anchorTop)) {
        const gap = 4;
        const resolvedAnchorRight = Number.isFinite(anchorRight)
          ? anchorRight
          : anchorLeft + (anchorWidth || 0);
        const resolvedAnchorBottom = Number.isFinite(anchorBottom)
          ? anchorBottom
          : anchorTop + (anchorHeight || 0);
        const spaceAbove = anchorTop - minTop;
        const spaceBelow = maxBottom - resolvedAnchorBottom;
        const fitsAbove = spaceAbove >= popupHeight + gap;
        const fitsBelow = spaceBelow >= popupHeight + gap;
        let direction;
        if (fitsBelow && !fitsAbove) direction = 'down';
        else if (fitsAbove && !fitsBelow) direction = 'up';
        else direction = spaceAbove > spaceBelow ? 'up' : 'down';
        const desiredLeft = Number.isFinite(anchorCenter)
          ? anchorCenter - popupWidth / 2
          : resolvedAnchorRight - popupWidth;
        const desiredTop =
          direction === 'up'
            ? anchorTop - popupHeight - gap
            : resolvedAnchorBottom + gap;
        const nextLeft = Math.min(
          Math.max(desiredLeft, minLeft),
          Math.max(minLeft, maxRight - popupWidth)
        );
        const nextTop = Math.min(
          Math.max(desiredTop, minTop),
          Math.max(minTop, maxBottom - popupHeight)
        );

        this.resolvedContextMenuPosition = {
          ...this.resolvedContextMenuPosition,
          left: fixedPosition(nextLeft, zoom),
          right: 'auto',
          top: fixedPosition(nextTop, zoom),
          bottom: 'auto',
          direction,
          transformOrigin: `${direction === 'up' ? 'bottom' : 'top'} center`,
        };
        return;
      }

      const nextLeft = Math.min(
        Math.max(rect.left, minLeft),
        Math.max(minLeft, maxRight - popupWidth)
      );
      const nextTop = Math.min(
        Math.max(rect.top, minTop),
        Math.max(minTop, maxBottom - popupHeight)
      );

      if (
        Math.abs(nextLeft - rect.left) < 0.5 &&
        Math.abs(nextTop - rect.top) < 0.5
      ) {
        return;
      }

      this.resolvedContextMenuPosition = {
        ...this.resolvedContextMenuPosition,
        left: fixedPosition(nextLeft, zoom),
        right: 'auto',
        top: fixedPosition(nextTop, zoom),
        bottom: 'auto',
      };
    },
    focusPopupForKeyboard() {
      if (window.matchMedia?.('(pointer: coarse)').matches) return;
      const firstButton = this.$refs.aceleresPopupRoot?.querySelector('button');
      firstButton?.focus?.({ preventScroll: true });
    },
    handleTranslate() {
      const { locale: accountLocale } = this.getAccount(this.currentAccountId);
      const agentLocale = this.getUISettings?.locale;
      const targetLanguage = agentLocale || accountLocale || 'en';
      this.$store.dispatch('translateMessage', {
        conversationId: this.conversationId,
        messageId: this.messageId,
        targetLanguage,
      });
      useTrack(CONVERSATION_EVENTS.TRANSLATE_A_MESSAGE);
      this.handleClose();
    },
    handleReplyTo() {
      this.$emit('replyTo', this.message);
      this.handleClose();
    },
    openEditModal() {
      const raw = this.messageContent || '';
      this.editContent = raw;
      this.editOriginalContent = raw;
      this.editSubmitting = false;
      this.closeEditEmojiPicker();
      this.showEmojiPicker = false;
      this.showEditModal = true;
    },
    closeEditModal() {
      this.showEditModal = false;
      this.closeEditEmojiPicker();
      this.editSubmitting = false;
    },
    toggleEditEmojiPicker() {
      if (this.showEditEmojiPicker) {
        this.closeEditEmojiPicker();
        return;
      }
      // Aceleres: computa posição fixa baseada no botão de emoji do modal.
      // Teleportamos o picker pro body pra não ser recortado pelo overflow-auto
      // do modal-container. Usa o mesmo tamanho do seletor principal e, em
      // telas pequenas, o próprio componente se ajusta à viewport.
      this.$nextTick(() => {
        const btn = this.$refs.aceleresEditEmojiBtn;
        const PANEL_W = 420;
        const GAP = 8;
        if (btn && btn.getBoundingClientRect) {
          const rect = btn.getBoundingClientRect();
          const maxLeft = window.innerWidth - PANEL_W - 8;
          this.editEmojiPickerStyle = {
            position: 'fixed',
            bottom: `${window.innerHeight - rect.top + GAP}px`,
            left: `${Math.max(8, Math.min(rect.left, maxLeft))}px`,
            zIndex: 10000, // acima do modal (z-9990)
          };
        } else {
          this.editEmojiPickerStyle = {
            position: 'fixed',
            bottom: '25vh',
            left: '50vw',
            transform: 'translateX(-50%)',
            zIndex: 10000,
          };
        }
        this.showEditEmojiPicker = true;
      });
      // Attach outside-click listener in CAPTURE phase — precisa rodar ANTES
      // do handler de mousedown do woot-modal (que fecha o modal em backdrop
      // click). Com stopPropagation aqui, o modal nunca vê o clique, então
      // fecha só o picker.
      document.addEventListener(
        'mousedown',
        this.handleEditEmojiOutsideClick,
        true
      );
    },
    closeEditEmojiPicker() {
      this.showEditEmojiPicker = false;
      this.editEmojiPickerStyle = null;
      document.removeEventListener(
        'mousedown',
        this.handleEditEmojiOutsideClick,
        true
      );
    },
    handleEditEmojiOutsideClick(event) {
      if (!this.showEditEmojiPicker) return;
      const panel = this.$refs.aceleresEditEmojiPanel;
      const btn = this.$refs.aceleresEditEmojiBtn;
      const target = event.target;
      if (panel && panel.contains(target)) return; // clique dentro do picker
      if (btn && btn.contains(target)) return; // clique no próprio botão (toggle cuida)
      this.closeEditEmojiPicker();
      event.stopPropagation();
      event.preventDefault();
    },
    handleEditEmoji(emoji) {
      // Inserção do emoji na posição do cursor (ou no fim como fallback).
      const ta = this.$refs.aceleresEditTextarea;
      if (ta && typeof ta.selectionStart === 'number') {
        const start = ta.selectionStart;
        const end = ta.selectionEnd;
        const before = this.editContent.slice(0, start);
        const after = this.editContent.slice(end);
        this.editContent = before + emoji + after;
        this.$nextTick(() => {
          const pos = start + emoji.length;
          try {
            ta.setSelectionRange(pos, pos);
            ta.focus();
          } catch (_) {
            /* ignore */
          }
        });
      } else {
        this.editContent = (this.editContent || '') + emoji;
      }
    },
    async confirmEdit() {
      if (this.editSubmitting || !this.hasEditChanged) return;
      this.editSubmitting = true;
      try {
        const updatedMessage = await this.$store.dispatch('editMessage', {
          conversationId: this.conversationId,
          messageId: this.messageId,
          content: this.editContent,
        });
        this.showEditModal = false;
        this.closeEditEmojiPicker();
        this.handleClose();

        if (this.isFailedMessage) {
          try {
            await this.$store.dispatch(
              'sendMessageWithData',
              useSnakeCase(updatedMessage)
            );
            useAlert('Mensagem atualizada e reenviada.');
          } catch (retryError) {
            const retryMsg =
              retryError?.response?.data?.error ||
              retryError?.response?.data?.message ||
              (typeof retryError?.message === 'string'
                ? retryError.message
                : '');
            useAlert(retryMsg || 'Mensagem atualizada, mas o reenvio falhou.');
          }
          return;
        }

        useAlert(
          'Mensagem editada. Pode levar alguns segundos até refletir no celular do cliente.'
        );
      } catch (error) {
        // O backend responde com mensagens de erro claras (15 min, deletada, etc)
        const backendMsg =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          (typeof error?.message === 'string' ? error.message : '');
        useAlert(backendMsg || 'Falha ao editar mensagem.');
      } finally {
        this.editSubmitting = false;
      }
    },
    openDeleteModal() {
      this.handleClose();
      this.deleteSubmitting = false;
      this.showDeleteModal = true;
    },
    async confirmDeletion() {
      if (this.deleteSubmitting) return;
      this.deleteSubmitting = true;
      try {
        const results = await Promise.allSettled(
          this.actionMessages.map(message =>
            this.$store.dispatch('deleteMessage', {
              conversationId: this.conversationId,
              messageId: message.id,
            })
          )
        );
        if (results.some(result => result.status === 'rejected')) {
          throw new Error('Some messages could not be deleted');
        }
        this.showDeleteModal = false;
        useAlert(this.$t('CONVERSATION.SUCCESS_DELETE_MESSAGE'));
        this.handleClose();
      } catch (error) {
        useAlert(this.$t('CONVERSATION.FAIL_DELETE_MESSSAGE'));
      } finally {
        this.deleteSubmitting = false;
      }
    },
    closeDeleteModal() {
      this.showDeleteModal = false;
    },
    // Aceleres: Reação de emoji
    async handleReact(emoji) {
      this.showEmojiPicker = false;
      this.handleClose();
      if (
        this.isSelectionMode &&
        window.matchMedia('(max-width: 767px)').matches
      ) {
        this.exitSelectionMode();
      }

      const currentUser = this.$store.getters.getCurrentUser;
      const currentUserId = this.currentUserId || currentUser?.id;
      const agentIdentity = `agent:${currentUserId}`;
      const previousReactions = (
        this.message.content_attributes?.reactions || []
      ).map(reaction => ({ ...reaction }));
      const isCurrentUserReaction = reaction =>
        Number(reaction.user_id ?? reaction.userId) === Number(currentUserId) ||
        (reaction.sender_external_id ?? reaction.senderExternalId) ===
          agentIdentity;
      const previousReaction = previousReactions.find(isCurrentUserReaction);
      const optimisticReactions = previousReactions.filter(
        reaction => !isCurrentUserReaction(reaction)
      );

      if (previousReaction?.emoji !== emoji) {
        optimisticReactions.push({
          user_id: currentUserId,
          name: currentUser?.name,
          emoji,
          sender_external_id: agentIdentity,
        });
      }

      this.$store.commit('UPDATE_MESSAGE_REACTIONS', {
        conversationId: this.conversationId,
        messageId: this.messageId,
        reactions: optimisticReactions,
      });

      try {
        const { data } = await AceleresMessageApi.react(
          this.currentAccountId,
          this.messageId,
          emoji
        );
        this.$store.commit('UPDATE_MESSAGE_REACTIONS', {
          conversationId: data.conversation_id,
          messageId: data.id,
          reactions: data.content_attributes?.reactions || [],
        });
      } catch (error) {
        this.$store.commit('UPDATE_MESSAGE_REACTIONS', {
          conversationId: this.conversationId,
          messageId: this.messageId,
          reactions: previousReactions,
        });
        useAlert(
          error?.response?.data?.error ||
            'Não foi possível sincronizar a reação com o WhatsApp.'
        );
      }
    },
    toggleEmojiPicker() {
      const shouldOpen = !this.showEmojiPicker;
      if (shouldOpen) {
        emitter.emit(BUS_EVENTS.EMOJI_PICKER_OPENED, {
          owner: 'message',
          messageId: this.messageId,
        });
      }
      this.showEmojiPicker = shouldOpen;
    },
    onEmojiPickerOpened({ owner, messageId } = {}) {
      const isCurrentPicker =
        owner === 'message' && Number(messageId) === Number(this.messageId);
      if (!isCurrentPicker && this.showEmojiPicker) {
        this.handleClose();
      }
    },
    // Aceleres: Reenviar
    handleForward() {
      this.handleClose();
      this.enterSelectionMode();
      this.actionMessages.forEach(message => {
        if (!this.isMessageSelected(message.id)) {
          this.toggleMessageSelection(message);
        }
      });
    },
    openForwardModal() {
      this.handleClose();
      this.showForwardModal = true;
    },
    // Aceleres: Fixar/desafixar
    async handlePin() {
      const wasPinned = !!this.enabledOptions.pinned;
      const togglePayloads = this.actionMessages.map(message => ({
        accountId: this.currentAccountId,
        conversationId: this.conversationId,
        messageId: message.id,
      }));
      const results = await Promise.allSettled(
        togglePayloads.map(payload =>
          this.$store.dispatch('pinMessage', payload)
        )
      );
      if (results.some(result => result.status === 'rejected')) {
        useAlert('Não foi possível fixar todas as imagens.');
        this.handleClose();
        return;
      }
      useAlert(
        wasPinned
          ? this.$t('CONVERSATION.CONTEXT_MENU.UNPINNED')
          : this.$t('CONVERSATION.CONTEXT_MENU.PINNED'),
        {
          type: 'button',
          message: 'Desfazer',
          duration: 5000,
          callback: () =>
            Promise.all(
              togglePayloads.map(payload =>
                this.$store.dispatch('pinMessage', payload)
              )
            ),
        }
      );
      this.handleClose();
    },
    // Aceleres: Destacar/remover destaque
    async handleStar() {
      const wasStarred = this.isStarred;
      const togglePayloads = this.actionMessages.map(message => ({
        accountId: this.currentAccountId,
        messageId: message.id,
      }));
      const results = await Promise.allSettled(
        togglePayloads.map(payload =>
          this.$store.dispatch('starMessage', payload)
        )
      );
      if (results.some(result => result.status === 'rejected')) {
        useAlert('Não foi possível destacar todas as imagens.');
        this.handleClose();
        return;
      }
      useAlert(
        wasStarred
          ? this.$t('CONVERSATION.CONTEXT_MENU.UNSTARRED')
          : this.$t('CONVERSATION.CONTEXT_MENU.STARRED'),
        {
          type: 'button',
          message: 'Desfazer',
          duration: 5000,
          callback: () =>
            Promise.all(
              togglePayloads.map(payload =>
                this.$store.dispatch('starMessage', payload)
              )
            ),
        }
      );
      this.handleClose();
    },
    // Aceleres: Adicionar texto à nota privada
    handleAddToNote() {
      emitter.emit(BUS_EVENTS.ADD_MESSAGE_TO_NOTE, {
        content: this.plainTextContent,
        conversationId: this.conversationId,
      });
      useAlert(this.$t('CONVERSATION.CONTEXT_MENU.ADDED_TO_NOTE'));
      this.handleClose();
    },
    // Aceleres: Info da mensagem — abre modal com timestamps e status
    handleMessageInfo() {
      this.handleClose();
      this.showMessageInfoModal = true;
      document.addEventListener('keydown', this.handleMessageInfoEscape);
      this.$nextTick(() => this.$refs.messageInfoDrawer?.focus());
      this.loadMessageInfoPdfPreviews();
    },
    closeMessageInfoModal() {
      this.showMessageInfoModal = false;
      document.removeEventListener('keydown', this.handleMessageInfoEscape);
    },
    messageInfoReceiptName(receipt) {
      const participant = (receipt.participantJid || '').split('@')[0];
      return receipt.name || participant || 'Participante';
    },
    messageInfoReceiptInitial(receipt) {
      return this.messageInfoReceiptName(receipt).charAt(0).toUpperCase();
    },
    messageInfoAudioAttachment(attachment) {
      return {
        id: attachment.key,
        dataUrl: attachment.url,
        contentType: attachment.mimeType,
        fileType: 'audio',
      };
    },
    // Aceleres: o host de homolog nao tem poppler/mutool, entao o ActiveStorage
    // devolve thumb_url vazia pra PDF. A miniatura da 1a pagina e renderizada
    // no cliente com pdf.js, igual ao bubble do chat.
    async loadMessageInfoPdfPreviews() {
      const pending = this.messageInfoAttachments.filter(
        attachment =>
          attachment.isPdf &&
          attachment.url &&
          !this.messageInfoPdfPreviews[attachment.key] &&
          !this.messageInfoPdfLoading[attachment.key]
      );

      await Promise.all(
        pending.map(async attachment => {
          this.messageInfoPdfLoading[attachment.key] = true;
          try {
            const result = await createPdfFirstPagePreviewFromUrl(
              attachment.url
            );
            if (result?.preview) {
              this.messageInfoPdfPreviews[attachment.key] = {
                preview: result.preview,
                pageCount: Number(result.pageCount) || 0,
              };
            }
          } catch {
            // A miniatura e enriquecimento: se o PDF nao renderizar, o card
            // com nome, tipo e tamanho continua identificando o anexo.
          } finally {
            delete this.messageInfoPdfLoading[attachment.key];
          }
        })
      );
    },
    messageInfoPdfPreview(attachment) {
      return this.messageInfoPdfPreviews[attachment.key]?.preview || '';
    },
    messageInfoPdfPageLabel(attachment) {
      const pageCount =
        this.messageInfoPdfPreviews[attachment.key]?.pageCount || 0;
      if (!pageCount) return '';
      return pageCount === 1 ? '1 página' : `${pageCount} páginas`;
    },
    isMessageInfoPdfLoading(attachment) {
      return !!this.messageInfoPdfLoading[attachment.key];
    },
    handleMessageInfoEscape(event) {
      if (event.key === 'Escape') this.closeMessageInfoModal();
    },
    // Aceleres: Baixar — no álbum, baixa todos os attachments do grupo.
    async handleDownload() {
      const attachments = this.actionMessages.flatMap(message =>
        Array.isArray(message.attachments) ? message.attachments : []
      );
      if (!attachments.length) {
        useAlert('Nada para baixar');
        this.handleClose();
        return;
      }

      const downloads = await Promise.allSettled(
        attachments.map(async attachment => {
          const url = attachment.data_url || attachment.dataUrl;
          if (!url) throw new Error('Attachment URL unavailable');
          const response = await fetch(url, { credentials: 'omit' });
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return {
            blob: await response.blob(),
            url,
            name:
              attachment.fallback_title ||
              attachment.file_name ||
              decodeURIComponent(url.split('/').pop().split('?')[0]) ||
              'arquivo',
          };
        })
      );

      let downloadedCount = 0;
      downloads.forEach(result => {
        if (result.status === 'rejected') return;
        const a = document.createElement('a');
        a.href = URL.createObjectURL(result.value.blob);
        a.download = result.value.name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        downloadedCount += 1;
        setTimeout(() => URL.revokeObjectURL(a.href), 1500);
      });

      if (!downloadedCount) useAlert('Arquivo indisponível');
      this.handleClose();
    },
    // Aceleres: Reagir — abre direto o picker emoji full.
    openReactionPicker() {
      emitter.emit(BUS_EVENTS.EMOJI_PICKER_OPENED, {
        owner: 'message',
        messageId: this.messageId,
      });
      this.showEmojiPicker = true;
    },
  },
};
</script>

<template>
  <div class="context-menu">
    <!-- Add To Canned Responses -->
    <woot-modal
      v-if="isCannedResponseModalOpen && enabledOptions['cannedResponse']"
      v-model:show="isCannedResponseModalOpen"
      :on-close="hideCannedResponseModal"
    >
      <AddCannedModal
        :response-content="plainTextContent"
        :on-close="hideCannedResponseModal"
      />
    </woot-modal>
    <!-- Confirm Deletion: modal próprio, compacto e teletransportado para que
         a árvore/overflow da bolha nunca recorte a confirmação. -->
    <Teleport to="body">
      <div
        v-if="showDeleteModal && enabledOptions['delete']"
        class="fixed inset-0 z-[2147483600] flex items-center justify-center bg-black/35 p-4 backdrop-blur-[2px]"
        role="presentation"
      >
        <button
          type="button"
          class="absolute inset-0 h-full w-full cursor-default border-0 bg-transparent p-0"
          :aria-label="
            $t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.CANCEL')
          "
          @click="closeDeleteModal"
        />
        <section
          class="relative w-full max-w-md overflow-hidden rounded-2xl border border-n-weak bg-n-surface-1 text-left shadow-2xl"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="`delete-message-title-${messageId}`"
          :aria-describedby="`delete-message-description-${messageId}`"
          @click.stop
        >
          <button
            type="button"
            class="absolute right-4 top-4 flex size-8 items-center justify-center rounded-full text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12"
            :aria-label="
              $t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.CANCEL')
            "
            @click="closeDeleteModal"
          >
            <span class="i-lucide-x size-4" />
          </button>

          <div class="flex gap-4 px-5 pb-5 pt-6 pr-14">
            <span
              class="flex size-11 shrink-0 items-center justify-center rounded-full bg-n-ruby-3 text-n-ruby-9"
              aria-hidden="true"
            >
              <span class="i-lucide-trash-2 size-5" />
            </span>
            <div class="min-w-0 pt-0.5">
              <h3
                :id="`delete-message-title-${messageId}`"
                class="m-0 text-base font-semibold leading-6 text-n-slate-12"
              >
                {{ $t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.TITLE') }}
              </h3>
              <p
                :id="`delete-message-description-${messageId}`"
                class="mb-0 mt-1 text-sm leading-5 text-n-slate-10"
              >
                {{ deleteConfirmationText }}
              </p>
            </div>
          </div>

          <footer
            class="flex items-center justify-end gap-2 border-t border-n-weak bg-n-surface-2 px-5 py-4"
          >
            <button
              type="button"
              class="rounded-lg border border-n-weak bg-n-surface-1 px-4 py-2 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-1 disabled:opacity-50"
              :disabled="deleteSubmitting"
              @click="closeDeleteModal"
            >
              {{ $t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.CANCEL') }}
            </button>
            <button
              type="button"
              class="rounded-lg bg-n-ruby-9 px-4 py-2 text-sm font-semibold text-white hover:bg-n-ruby-10 disabled:cursor-wait disabled:opacity-60"
              :disabled="deleteSubmitting"
              @click="confirmDeletion"
            >
              <span
                v-if="deleteSubmitting"
                class="i-lucide-loader-circle mr-1 inline-block size-4 animate-spin align-text-bottom"
              />
              {{ $t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.DELETE') }}
            </button>
          </footer>
        </section>
      </div>
    </Teleport>
    <!-- Forward Modal (Aceleres) -->
    <ForwardMessageModal
      v-if="showForwardModal"
      :show="showForwardModal"
      :conversation-id="conversationId"
      :selected-messages="actionMessages"
      @cancel="showForwardModal = false"
      @update:show="showForwardModal = $event"
      @success="showForwardModal = false"
    />
    <!-- Info da mensagem (Aceleres): drawer no padrão WhatsApp Web. -->
    <Teleport to="body">
      <Transition name="aceleres-message-info-drawer">
        <div v-if="showMessageInfoModal" class="aceleres-message-info-layer">
          <button
            type="button"
            class="aceleres-message-info-backdrop"
            aria-label="Fechar informações da mensagem"
            @click="closeMessageInfoModal"
          />
          <aside
            ref="messageInfoDrawer"
            class="aceleres-message-info-drawer"
            role="dialog"
            aria-modal="true"
            aria-labelledby="aceleres-message-info-title"
            tabindex="-1"
          >
            <header class="aceleres-message-info-header">
              <button
                type="button"
                class="aceleres-message-info-close"
                aria-label="Fechar"
                @click="closeMessageInfoModal"
              >
                <span class="i-lucide-x" aria-hidden="true" />
              </button>
              <h3 id="aceleres-message-info-title">Informações da mensagem</h3>
            </header>

            <div class="aceleres-message-info-content">
              <section class="aceleres-message-info-preview">
                <div
                  class="aceleres-message-info-bubble"
                  :class="{
                    'is-outgoing': isOutgoingMessageInfo,
                    'is-incoming': !isOutgoingMessageInfo,
                    'has-audio': messageInfoHasAudio,
                  }"
                >
                  <template
                    v-for="attachment in messageInfoAttachments"
                    :key="attachment.key"
                  >
                    <img
                      v-if="attachment.type === 'image' && attachment.url"
                      :src="attachment.url"
                      :alt="attachment.name"
                      class="aceleres-message-info-image"
                    />
                    <AudioPresentation
                      v-else-if="attachment.type === 'audio' && attachment.url"
                      class="aceleres-message-info-audio"
                      :attachment="messageInfoAudioAttachment(attachment)"
                      :appearance="messageInfoAudioAppearance"
                      :is-outgoing="isOutgoingMessageInfo"
                      :avatar-name="messageInfoAudioSender.name"
                      :avatar-src="messageInfoAudioSender.src"
                      :forwarded="messageInfoAudioForwarded"
                      :show-message-meta="false"
                      compact
                    />
                    <video
                      v-else-if="attachment.type === 'video' && attachment.url"
                      class="aceleres-message-info-video"
                      controls
                      preload="metadata"
                    >
                      <source
                        :src="attachment.url"
                        :type="attachment.mimeType"
                      />
                    </video>
                    <a
                      v-else
                      :href="attachment.url || undefined"
                      :target="attachment.url ? '_blank' : undefined"
                      :rel="attachment.url ? 'noopener noreferrer' : undefined"
                      class="aceleres-message-info-attachment"
                      :class="{ 'is-unavailable': !attachment.url }"
                      :title="attachment.name"
                      @click.stop
                    >
                      <span
                        v-if="attachment.isPdf && attachment.url"
                        class="aceleres-message-info-attachment-preview"
                        :class="{
                          'is-loading': isMessageInfoPdfLoading(attachment),
                        }"
                      >
                        <img
                          v-if="messageInfoPdfPreview(attachment)"
                          :src="messageInfoPdfPreview(attachment)"
                          alt=""
                        />
                      </span>
                      <span class="aceleres-message-info-attachment-file">
                        <span
                          class="aceleres-message-info-attachment-icon"
                          aria-hidden="true"
                        >
                          <FileIcon :file-type="attachment.extension" />
                        </span>
                        <span class="aceleres-message-info-attachment-details">
                          <span class="aceleres-message-info-attachment-name">
                            {{ attachment.name }}
                          </span>
                          <span class="aceleres-message-info-attachment-meta">
                            <span v-if="messageInfoPdfPageLabel(attachment)">
                              {{ messageInfoPdfPageLabel(attachment) }}
                            </span>
                            <span>{{ attachment.typeLabel }}</span>
                            <span v-if="attachment.sizeLabel">
                              {{ attachment.sizeLabel }}
                            </span>
                          </span>
                        </span>
                      </span>
                    </a>
                  </template>
                  <p v-if="plainTextContent" class="aceleres-message-info-text">
                    {{ plainTextContent }}
                  </p>
                  <p
                    v-else-if="!messageInfoAttachments.length"
                    class="aceleres-message-info-text"
                  >
                    {{ messageInfo.labels.emptyContent }}
                  </p>
                  <span class="aceleres-message-info-time">
                    {{ messageInfo.sentAt || 'Horário indisponível' }}
                  </span>
                </div>
              </section>

              <section class="aceleres-message-info-statuses">
                <template v-if="messageInfo.isGroup">
                  <article class="aceleres-message-info-status">
                    <span
                      class="i-lucide-send aceleres-message-info-status-icon"
                      aria-hidden="true"
                    />
                    <div>
                      <h4>{{ messageInfo.labels.sent }}</h4>
                      <p>{{ messageInfo.sentAt || 'Horário indisponível' }}</p>
                    </div>
                  </article>

                  <article
                    class="aceleres-message-info-status"
                    :class="{ 'is-pending': !messageInfo.hasDelivered }"
                  >
                    <span
                      class="i-lucide-check-check aceleres-message-info-status-icon"
                      aria-hidden="true"
                    />
                    <div>
                      <h4>{{ messageInfo.labels.delivered }}</h4>
                      <p>{{ messageInfo.deliveredDisplay }}</p>
                    </div>
                  </article>

                  <article
                    class="aceleres-message-info-status"
                    :class="{
                      'is-read': messageInfo.hasRead,
                      'is-pending': !messageInfo.hasRead,
                    }"
                  >
                    <span
                      class="i-lucide-check-check aceleres-message-info-status-icon"
                      aria-hidden="true"
                    />
                    <div>
                      <h4>{{ messageInfo.labels.readBy }}</h4>
                      <p v-if="!messageInfo.readReceipts.length">
                        {{ messageInfo.readDisplay }}
                      </p>
                    </div>
                  </article>

                  <article
                    v-for="receipt in messageInfo.readReceipts"
                    :key="receipt.participantJid"
                    class="flex items-center gap-3 border-b border-n-weak py-4 last:border-b-0"
                  >
                    <img
                      v-if="receipt.avatarUrl"
                      :src="receipt.avatarUrl"
                      :alt="messageInfoReceiptName(receipt)"
                      class="size-10 shrink-0 rounded-full object-cover"
                    />
                    <span
                      v-else
                      class="flex size-10 shrink-0 items-center justify-center rounded-full bg-n-brand/15 text-sm font-medium text-n-brand"
                      aria-hidden="true"
                    >
                      {{ messageInfoReceiptInitial(receipt) }}
                    </span>
                    <div class="min-w-0">
                      <h4
                        class="m-0 truncate text-base font-medium text-n-slate-12"
                      >
                        {{ messageInfoReceiptName(receipt) }}
                      </h4>
                      <p class="m-0.5 text-sm text-n-slate-10">
                        {{ receipt.readAt || 'Horário indisponível' }}
                      </p>
                    </div>
                  </article>
                </template>

                <template v-else>
                  <article class="aceleres-message-info-status">
                    <span
                      class="i-lucide-send aceleres-message-info-status-icon"
                      aria-hidden="true"
                    />
                    <div>
                      <h4>{{ messageInfo.labels.sent }}</h4>
                      <p>{{ messageInfo.sentAt || 'Horário indisponível' }}</p>
                    </div>
                  </article>

                  <article
                    class="aceleres-message-info-status"
                    :class="{ 'is-pending': !messageInfo.hasDelivered }"
                  >
                    <span
                      class="i-lucide-check-check aceleres-message-info-status-icon"
                      aria-hidden="true"
                    />
                    <div>
                      <h4>{{ messageInfo.labels.delivered }}</h4>
                      <p>{{ messageInfo.deliveredDisplay }}</p>
                    </div>
                  </article>

                  <article
                    class="aceleres-message-info-status"
                    :class="{
                      'is-read': messageInfo.hasRead,
                      'is-pending': !messageInfo.hasRead,
                    }"
                  >
                    <span
                      class="i-lucide-check-check aceleres-message-info-status-icon"
                      aria-hidden="true"
                    />
                    <div>
                      <h4>{{ messageInfo.labels.read }}</h4>
                      <p>{{ messageInfo.readDisplay }}</p>
                    </div>
                  </article>
                </template>
              </section>
            </div>
          </aside>
        </div>
      </Transition>
    </Teleport>

    <NextButton
      v-if="!hideButton"
      ghost
      slate
      sm
      icon="i-lucide-chevron-down"
      class="invisible group-hover/context-menu:visible"
      @click="handleOpen"
    />
    <!-- Aceleres: Teleport direto pro body com position:fixed PURA — sem
         clamping de viewport, sem focus-steal, sem scroll-lock. A posição
         vem direto do BCR do chevron calculado em Message.vue. Click fora
         fecha (listener no document). -->
    <Teleport to="body">
      <Transition name="aceleres-message-menu">
        <div
          v-if="isOpen && !isCannedResponseModalOpen"
          ref="aceleresPopupRoot"
          class="aceleres-popup-root"
          :class="{ 'is-emoji-picker': showEmojiPicker }"
          :style="{
            left: resolvedContextMenuPosition.left || 'auto',
            right: resolvedContextMenuPosition.right || 'auto',
            top: resolvedContextMenuPosition.top || 'auto',
            bottom: resolvedContextMenuPosition.bottom || 'auto',
            transformOrigin:
              resolvedContextMenuPosition.transformOrigin || 'top left',
          }"
          role="dialog"
          :aria-label="popupAriaLabel"
          tabindex="-1"
          @click.stop
        >
          <div
            class="aceleres-wa-stack"
            :class="[
              `is-${resolvedContextMenuPosition.direction || 'down'}`,
              { 'is-emoji-picker-open': showEmojiPicker },
            ]"
          >
            <div v-if="showEditModal" class="aceleres-edit-modal">
              <div class="aceleres-edit-modal__header">
                <button
                  type="button"
                  class="aceleres-edit-modal__close"
                  aria-label="Fechar"
                  @click.stop="closeEditModal"
                >
                  <fluent-icon icon="dismiss" size="18" />
                </button>
                <h3 class="aceleres-edit-modal__title">{{ editModalTitle }}</h3>
              </div>

              <div class="aceleres-edit-modal__body">
                <textarea
                  ref="aceleresEditTextarea"
                  v-model="editContent"
                  class="aceleres-edit-modal__textarea"
                  rows="4"
                  placeholder="Digite a nova mensagem…"
                  @keydown.ctrl.enter.prevent="confirmEdit"
                  @keydown.meta.enter.prevent="confirmEdit"
                />
                <p class="aceleres-edit-modal__hint">
                  {{ editModalHint }}
                </p>
              </div>

              <div class="aceleres-edit-modal__footer">
                <div class="aceleres-edit-modal__emoji-wrap">
                  <button
                    ref="aceleresEditEmojiBtn"
                    type="button"
                    class="aceleres-edit-modal__emoji-btn"
                    :class="{ 'is-active': showEditEmojiPicker }"
                    aria-label="Emojis"
                    @click.stop="toggleEditEmojiPicker"
                  >
                    <span
                      class="i-lucide-smile-plus flex-shrink-0"
                      style="width: 20px; height: 20px"
                    />
                  </button>
                  <Teleport to="body">
                    <div
                      v-if="showEditEmojiPicker"
                      ref="aceleresEditEmojiPanel"
                      class="fixed z-[10000]"
                      :style="editEmojiPickerStyle"
                      @click.stop
                      @mousedown.stop
                    >
                      <WhatsAppEmojiPicker :on-click="handleEditEmoji" />
                    </div>
                  </Teleport>
                </div>
                <div class="aceleres-edit-modal__spacer" />
                <button
                  type="button"
                  class="aceleres-edit-modal__cancel"
                  @click.stop="closeEditModal"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  class="aceleres-edit-modal__send"
                  :disabled="editSubmitting || !hasEditChanged"
                  @click.stop="confirmEdit"
                >
                  <fluent-icon icon="send" size="16" />
                  <span>{{ editSubmitLabel }}</span>
                </button>
              </div>
            </div>
            <div v-else-if="!showEmojiPicker" class="aceleres-menu-container">
              <!-- Ordem WhatsApp: Info, Responder, Copiar, Reagir, Baixar,
             Reenviar, Fixar, Destacar e Adicionar nota.
             enabledOptions decide visibilidade por tipo de mensagem (texto,
             imagem, áudio). Lógica em Message.vue::contextMenuEnabledOptions. -->
              <!-- 1. Info da mensagem -->
              <MenuItem
                v-if="enabledOptions['info']"
                :option="{ icon: 'info', label: 'Info. da mensagem' }"
                variant="icon"
                @click.stop="handleMessageInfo"
              />
              <!-- 2. Responder -->
              <MenuItem
                v-if="enabledOptions['replyTo']"
                :option="{ icon: 'arrow-reply', label: 'Responder' }"
                variant="icon"
                @click.stop="handleReplyTo"
              />
              <!-- 3. Copiar -->
              <MenuItem
                v-if="enabledOptions['copy']"
                :option="{ icon: 'copy', label: 'Copiar' }"
                variant="icon"
                @click.stop="handleCopy"
              />
              <!-- 4. Reagir -->
              <MenuItem
                v-if="enabledOptions['react']"
                :option="{ icon: 'emoji-add', label: 'Reagir' }"
                variant="icon"
                @click.stop="openReactionPicker"
              />
              <!-- 5. Baixar (apenas imagens/áudios/arquivos) -->
              <MenuItem
                v-if="enabledOptions['download']"
                :option="{ icon: 'arrow-download', label: 'Baixar' }"
                variant="icon"
                @click.stop="handleDownload"
              />
              <!-- 6. Reenviar -->
              <MenuItem
                v-if="enabledOptions['forward']"
                :option="{
                  icon: 'share',
                  label: $t('CONVERSATION.CONTEXT_MENU.FORWARD'),
                }"
                variant="icon"
                @click.stop="handleForward"
              />

              <!-- 7. Fixar / Desafixar -->
              <MenuItem
                :option="{
                  icon: 'attach',
                  label: enabledOptions['pinned']
                    ? $t('CONVERSATION.CONTEXT_MENU.UNPIN')
                    : $t('CONVERSATION.CONTEXT_MENU.PIN'),
                }"
                variant="icon"
                @click.stop="handlePin"
              />
              <!-- 8. Destacar / Remover destaque -->
              <MenuItem
                :option="{
                  icon: 'star-emphasis',
                  label: isStarred
                    ? $t('CONVERSATION.CONTEXT_MENU.UNSTAR')
                    : $t('CONVERSATION.CONTEXT_MENU.STAR'),
                }"
                variant="icon"
                @click.stop="handleStar"
              />
              <!-- 9. Adicionar nota (só texto, não em mídia/áudio) -->
              <MenuItem
                v-if="enabledOptions['addToNote']"
                :option="{ icon: 'document', label: 'Adicionar nota' }"
                variant="icon"
                @click.stop="handleAddToNote"
              />

              <!-- 10. Editar mensagem (só outgoing WhatsApp texto até 15 min) -->
              <MenuItem
                v-if="enabledOptions['edit']"
                :option="{ icon: 'edit', label: editMenuLabel }"
                variant="icon"
                @click.stop="openEditModal"
              />

              <!-- Instagram: moderação do comentário na publicação -->
              <hr v-if="canModerateComment" />

              <MenuItem
                v-if="canModerateComment && !isCommentHidden"
                :option="{ icon: 'eye-hide', label: 'Ocultar comentário' }"
                variant="icon"
                @click.stop="moderateInstagramComment('hide')"
              />

              <MenuItem
                v-if="canModerateComment && isCommentHidden"
                :option="{ icon: 'eye-show', label: 'Reexibir comentário' }"
                variant="icon"
                @click.stop="moderateInstagramComment('unhide')"
              />

              <MenuItem
                v-if="canModerateComment"
                :option="{ icon: 'delete', label: 'Excluir comentário' }"
                variant="icon"
                class="aceleres-menu-danger"
                @click.stop="moderateInstagramComment('delete')"
              />

              <hr v-if="enabledOptions['delete']" />

              <!-- 10. Excluir -->
              <MenuItem
                v-if="enabledOptions['delete']"
                :option="{
                  icon: 'delete',
                  label: $t('CONVERSATION.CONTEXT_MENU.DELETE'),
                }"
                variant="icon"
                class="aceleres-menu-danger"
                @click.stop="openDeleteModal"
              />
            </div>
            <div
              v-if="!showEditModal && !initialShowPicker"
              class="aceleres-reaction-pill"
            >
              <MessageReactionBar
                @react="handleReact"
                @more="toggleEmojiPicker"
                @close="handleClose"
              />
            </div>
            <div
              v-if="showEmojiPicker"
              class="aceleres-message-emoji-picker-card"
            >
              <div class="aceleres-message-emoji-picker-body">
                <WhatsAppEmojiPicker
                  embedded
                  show-recent-reactions
                  :on-select="handleReact"
                  @click.stop
                />
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
.aceleres-message-info-layer {
  position: fixed;
  inset: 0;
  z-index: 2147483500;
}

.aceleres-message-info-backdrop {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: default;
}

.aceleres-message-info-drawer {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  width: min(460px, 100%);
  height: 100%;
  overflow: hidden;
  outline: none;
  background: #f0f2f5;
  box-shadow: -10px 0 30px rgba(15, 23, 42, 0.16);
}

.aceleres-message-info-header {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  min-height: 72px;
  padding: 0 20px;
  gap: 22px;
  background: #f0f2f5;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
}

.aceleres-message-info-header h3 {
  margin: 0;
  color: #111b21;
  font-size: 17px;
  font-weight: 500;
  line-height: 1.2;
}

.aceleres-message-info-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  padding: 0;
  border: 0;
  border-radius: 9999px;
  color: #54656f;
  background: transparent;
  cursor: pointer;
}

.aceleres-message-info-close:hover {
  background: rgba(15, 23, 42, 0.07);
}

.aceleres-message-info-close span {
  width: 22px;
  height: 22px;
}

.aceleres-message-info-content {
  min-height: 0;
  flex: 1 1 auto;
  overflow-y: auto;
  background: #f0f2f5;
}

.aceleres-message-info-preview {
  display: flex;
  align-items: center;
  min-height: 190px;
  padding: 28px 24px;
  background-color: #efeae2;
  background-image: url('/assets/images/aceleres-chat-pattern.svg');
  background-position: center;
  background-repeat: repeat;
  background-size: 160px 160px;
}

.aceleres-message-info-bubble {
  position: relative;
  width: fit-content;
  min-width: 170px;
  max-width: 92%;
  padding: 10px 12px 24px;
  border-radius: 9px;
  color: #111b21;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.13);
}

.aceleres-message-info-bubble.is-outgoing {
  margin-left: auto;
  background: #d9fdd3;
}

.aceleres-message-info-bubble.is-incoming {
  margin-right: auto;
}

.aceleres-message-info-bubble.has-audio {
  width: 92%;
  min-width: 0;
}

.aceleres-message-info-text {
  margin: 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  font-size: 14px;
  line-height: 1.45;
}

.aceleres-message-info-time {
  position: absolute;
  right: 9px;
  bottom: 6px;
  max-width: calc(100% - 18px);
  overflow: hidden;
  color: #667781;
  font-size: 10px;
  line-height: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.aceleres-message-info-image {
  display: block;
  width: 100%;
  max-height: 260px;
  margin-bottom: 8px;
  border-radius: 7px;
  object-fit: contain;
}

.aceleres-message-info-audio,
.aceleres-message-info-video {
  display: block;
  width: 100%;
  margin-bottom: 8px;
  border-radius: 7px;
}

.aceleres-message-info-audio {
  min-width: 0;
  max-width: 100%;
}

.aceleres-message-info-bubble.has-audio .aceleres-message-info-audio {
  width: 100%;
  min-width: 0;
  max-width: 100%;
}

.aceleres-message-info-bubble.has-audio :deep(.acl-wa-audio__column) {
  transform: none;
}

.aceleres-message-info-bubble.has-audio :deep(.acl-wa-audio__waveform) {
  overflow: hidden;
}

.aceleres-message-info-video {
  max-height: 260px;
  background: #111b21;
}

.aceleres-message-info-attachment {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 230px;
  max-width: 100%;
  margin-bottom: 6px;
  padding: 12px;
  border-radius: 7px;
  background: rgba(255, 255, 255, 0.62);
  color: inherit;
  font-size: 13px;
  text-decoration: none;
  overflow-wrap: anywhere;
}

.aceleres-message-info-attachment.is-unavailable {
  cursor: default;
  opacity: 0.72;
  pointer-events: none;
}

.aceleres-message-info-attachment-preview {
  display: block;
  overflow: hidden;
  border-radius: 6px;
  background: #ffffff;
  box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.08);
}

.aceleres-message-info-attachment-preview.is-loading {
  min-height: 120px;
  background-color: rgba(15, 23, 42, 0.06);
  background-image: linear-gradient(
    100deg,
    rgba(255, 255, 255, 0) 30%,
    rgba(255, 255, 255, 0.75) 50%,
    rgba(255, 255, 255, 0) 70%
  );
  background-size: 220% 100%;
  animation: aceleres-message-info-shimmer 1.2s linear infinite;
}

.aceleres-message-info-attachment-preview img {
  display: block;
  width: 100%;
  max-height: 260px;
  object-fit: cover;
  object-position: top center;
}

.aceleres-message-info-attachment-file {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
}

.aceleres-message-info-attachment-icon {
  display: inline-flex;
  width: 28px;
  height: 32px;
  flex: 0 0 28px;
  align-items: center;
  justify-content: center;
}

.aceleres-message-info-attachment-icon > * {
  width: 26px;
  height: 30px;
}

.aceleres-message-info-attachment-details {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
}

.aceleres-message-info-attachment-name {
  font-weight: 500;
  line-height: 1.3;
  overflow-wrap: anywhere;
}

.aceleres-message-info-attachment-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  color: rgba(15, 23, 42, 0.55);
  font-size: 11px;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.aceleres-message-info-attachment-meta > span + span::before {
  content: '·';
  margin-right: 6px;
}

@keyframes aceleres-message-info-shimmer {
  from {
    background-position: 180% 0;
  }

  to {
    background-position: -80% 0;
  }
}

.aceleres-message-info-statuses {
  margin-top: 10px;
  padding: 8px 26px 24px;
  background: #ffffff;
}

.aceleres-message-info-status {
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr);
  gap: 12px;
  padding: 21px 0;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
}

.aceleres-message-info-status:last-child {
  border-bottom: 0;
}

.aceleres-message-info-status-icon {
  width: 23px;
  height: 23px;
  margin-top: 1px;
  color: #667781;
}

.aceleres-message-info-status.is-read .aceleres-message-info-status-icon {
  color: #53bdeb;
}

.aceleres-message-info-status.is-pending .aceleres-message-info-status-icon,
.aceleres-message-info-status.is-pending h4 {
  color: #9aa5aa;
}

.aceleres-message-info-status h4 {
  margin: 0;
  color: #111b21;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.35;
}

.aceleres-message-info-status p {
  margin: 4px 0 0;
  color: #667781;
  font-size: 13px;
  line-height: 1.4;
}

.aceleres-message-info-drawer-enter-active,
.aceleres-message-info-drawer-leave-active {
  transition: opacity 0.2s ease;
}

.aceleres-message-info-drawer-enter-active .aceleres-message-info-drawer,
.aceleres-message-info-drawer-leave-active .aceleres-message-info-drawer {
  transition: transform 0.24s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.aceleres-message-info-drawer-enter-from,
.aceleres-message-info-drawer-leave-to {
  opacity: 0;
}

.aceleres-message-info-drawer-enter-from .aceleres-message-info-drawer,
.aceleres-message-info-drawer-leave-to .aceleres-message-info-drawer {
  transform: translateX(100%);
}

:global(.dark) .aceleres-message-info-drawer,
:global(.dark) .aceleres-message-info-header,
:global(.dark) .aceleres-message-info-content {
  background: #111b21;
}

:global(.dark) .aceleres-message-info-header h3,
:global(.dark) .aceleres-message-info-status h4 {
  color: #e9edef;
}

:global(.dark) .aceleres-message-info-statuses {
  background: #202c33;
}

:global(.dark) .aceleres-message-info-close,
:global(.dark) .aceleres-message-info-status p {
  color: #aebac1;
}

@media (max-width: 640px) {
  .aceleres-message-info-drawer {
    width: 100%;
  }
}

.aceleres-menu-container {
  @apply bg-white dark:bg-n-solid-2 overflow-hidden;
  min-width: 200px;
  max-width: 220px;
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow:
    0 8px 20px rgba(0, 0, 0, 0.18),
    0 2px 6px rgba(0, 0, 0, 0.06);
  padding: 0;

  hr:first-child {
    @apply hidden;
  }

  hr {
    @apply m-0 border-0 border-t border-solid border-n-weak;
    margin: 4px 0;
  }

  // Menu items: compacto nas laterais, legível na vertical.
  :deep(.menu) {
    @apply text-n-slate-12;
    width: 100%;
    min-width: 200px;
    padding: 7px 12px;
    gap: 10px;
    border-radius: 0;
    cursor: pointer;
    transition: background-color 0.12s ease;
  }
  :deep(.menu:hover) {
    @apply bg-transparent text-n-slate-12;
    background: rgba(0, 0, 0, 0.04);
    color: inherit;
  }
  :deep(.menu .menu-label) {
    @apply font-normal;
    font-size: 15px;
    line-height: 1.2;
    margin: 0;
  }
  // Ícone fluent (default size=14 do MenuItem) — override pra 18x18 só aqui.
  :deep(.menu svg) {
    width: 18px;
    height: 18px;
  }
}

:deep(.aceleres-menu-danger .menu) {
  color: rgb(220, 38, 38);
}
:deep(.aceleres-menu-danger .menu:hover) {
  background: rgba(220, 38, 38, 0.08) !important;
  color: rgb(220, 38, 38) !important;
}

.aceleres-reaction-bar {
  @apply bg-transparent;
}

/* Raiz teleportada para o body, ancorada no clique e corrigida depois da
   renderização para nunca escapar da visual viewport. */
.aceleres-popup-root {
  position: fixed;
  z-index: 9999;
  outline: none;
  will-change: transform, opacity;
}
.aceleres-message-menu-enter-active,
.aceleres-message-menu-leave-active {
  transition:
    opacity 0.13s ease,
    transform 0.16s cubic-bezier(0.16, 1, 0.3, 1);
}
.aceleres-message-menu-enter-from,
.aceleres-message-menu-leave-to {
  opacity: 0;
  transform: scale(0.9) translateY(3px);
}
@keyframes aceleres-popup-in {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Aceleres: a posição da barra segue a metade da conversa em que está a
   seta. Seta embaixo: reações acima e ações junto à mensagem. Seta em cima:
   ações junto à mensagem e reações abaixo. */
.aceleres-wa-stack {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}
.aceleres-wa-stack.is-up {
  flex-direction: column-reverse;
}
.aceleres-wa-stack.is-emoji-picker-open {
  flex-direction: column;
}
.aceleres-wa-stack.is-up.is-emoji-picker-open {
  flex-direction: column-reverse;
}
.aceleres-reaction-pill {
  @apply bg-white dark:bg-n-solid-2;
  border-radius: 9999px;
  padding: 2px 4px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow:
    0 8px 20px rgba(0, 0, 0, 0.18),
    0 2px 4px rgba(0, 0, 0, 0.06);
  display: inline-flex;
}
.aceleres-reaction-pill :deep(.aceleres-emoji-btn) {
  width: 40px;
  height: 40px;
  font-size: 24px;
}
.aceleres-reaction-pill :deep(.aceleres-emoji-btn span) {
  width: 26px !important;
  height: 26px !important;
  font-size: 26px !important;
}
.aceleres-reaction-pill
  :deep(.aceleres-emoji-btn:not(.aceleres-emoji-btn--more) .cw-emoji) {
  display: block !important;
  width: 26px !important;
  height: 26px !important;
}
.aceleres-message-emoji-picker-card {
  @apply dark:bg-n-solid-2;
  background: #f7f8fa;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow:
    0 12px 28px rgba(0, 0, 0, 0.2),
    0 2px 4px rgba(0, 0, 0, 0.06);
  width: 420px;
  max-width: 92vw;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.aceleres-message-emoji-picker-body {
  /* Wrapper do EmojiInput. Esse componente foi feito pra abrir flutuando em
     cima de um input box (position:absolute, top:-95px, w-80, etc). Aqui ele
     vai EMBUTIDO no nosso popup, então normalizamos tudo via :deep.
     overflow:hidden mata qualquer scroll horizontal residual; a área de
     emojis cuida do seu próprio scroll vertical. */
  position: relative;
  width: 100%;
  overflow: hidden;

  :deep(.emoji-dialog) {
    position: static !important;
    width: 100% !important;
    height: auto !important;
    max-height: none !important;
    top: auto !important;
    right: auto !important;
    left: auto !important;
    box-shadow: none !important;
    outline: none !important;
    border: 0 !important;
    border-radius: 0 !important;
    box-sizing: border-box !important;
    background: transparent !important;
    z-index: auto !important;
    overflow: hidden !important;
  }
  /* Aceleres: reordena o flex interno do EmojiInput pra:
       1. footer (tabs por categoria + clock de recentes) no TOPO
       2. search row no meio
       3. grid de emojis embaixo
     CSS-only — DOM continua igual, só mexemos no order. (2026-07-01) */
  :deep(.emoji-dialog > .flex.flex-col) {
    width: 100% !important;
    box-sizing: border-box !important;
    overflow: hidden;
    display: flex !important;
    flex-direction: column;
  }
  :deep(.emoji-dialog > .flex.flex-col > .emoji-dialog--footer) {
    order: -1;
  }
  /* A setinha pseudo-elemento apontava pra um botão fora; aqui não faz
     sentido — esconde. */
  :deep(.emoji-dialog::before) {
    display: none !important;
  }
  /* Search box ocupa toda largura, padding mais respirado. */
  :deep(.emoji-dialog > .flex > .flex.gap-2) {
    margin: 10px 12px 6px 12px !important;
    z-index: 20 !important;
    background: #f7f8fa !important;
  }
  /* Aceleres: input de busca em formato PÍLULA (border-radius 50px) com
     lupa SVG embutida como background-image no canto esquerdo — mimica
     o estilo de busca do WhatsApp Web. (pedido 2026-07-01) */
  :deep(.emoji-dialog input[type='text']) {
    width: 100%;
    border-radius: 50px !important;
    padding-left: 32px !important;
    padding-right: 12px !important;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23667085' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='11' cy='11' r='8'/%3E%3Cpath d='m21 21-4.3-4.3'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: 10px center;
    background-size: 14px 14px;
  }
  /* Grid de emojis: rola na vertical, NUNCA horizontal. Altura fixa pra
     evitar que o picker estique sem fim. */
  :deep(.emoji-item) {
    height: 320px !important;
    max-height: 320px !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    padding: 0 8px 8px 8px;
    box-sizing: border-box !important;
    overscroll-behavior: contain;
    /* scroll suave + barra fininha em browsers WebKit */
    scrollbar-width: thin;
  }
  /* Continuous mode (scroll-sync-tabs) — section title vira sticky pra
     ficar visível enquanto rola a categoria, igual WhatsApp. */
  :deep(.emoji-item--continuous .emoji--section-title) {
    position: sticky;
    top: 0;
    z-index: 1;
    background: #f7f8fa;
    padding: 6px 4px !important;
    font-size: 11px !important;
    font-weight: 600 !important;
    color: rgba(0, 0, 0, 0.55) !important;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }
  :deep(.emoji-item::-webkit-scrollbar) {
    width: 8px;
  }
  :deep(.emoji-item::-webkit-scrollbar-thumb) {
    background: rgba(0, 0, 0, 0.15);
    border-radius: 4px;
  }
  :deep(.emoji-item::-webkit-scrollbar-thumb:hover) {
    background: rgba(0, 0, 0, 0.25);
  }
  /* Aceleres: grid de emojis vira CSS Grid de 8 colunas fixas (1fr cada)
     em vez de flex-wrap. flex-wrap deixava a última coluna vazia porque
     a soma dos items (34px * 8 + gap) não batia com a largura interna e
     o navegador arredondava pra 7 items por linha. Com grid 8x1fr, cada
     célula fica com 1/8 da largura útil — a 8ª coluna PREENCHE. Também
     aumenta um pouco o tamanho do emoji dentro (font-size + height).
     (pedido 2026-07-01) */
  :deep(.emoji--row) {
    display: grid !important;
    grid-template-columns: repeat(8, 1fr);
    gap: 2px;
    padding: 0 !important;
  }
  :deep(.emoji--row .emoji--item) {
    width: 100% !important;
    height: 38px !important;
    font-size: 24px !important;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    transition: background 0.1s;
    /* Bandeiras e emojis "wide" não devem vazar pra fora do quadrado. */
    flex-shrink: 0;
    overflow: hidden;
  }
  /* Footer (tabs por categoria) — reposicionado no TOPO via `order: -1`
     acima. Sem cor de fundo, sem borda, padding vertical maior. Padding
     horizontal maior também pra ícones não colarem nas laterais.
     (pedido 2026-07-01) */
  :deep(.emoji-dialog--footer) {
    background: #f7f8fa !important;
    border-bottom: 0 !important;
    z-index: 30 !important;
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    padding: 10px 14px !important;
  }
  /* Aceleres: o primeiro tab representa o histórico de reações recentes.
     Esconde o ícone original e mantém o relógio via máscara, herdando a cor
     do botão para que os estados ativo e hover continuem funcionando. */
  :deep(.emoji-dialog--footer li:first-child .emoji--item) {
    position: relative;
  }
  :deep(.emoji-dialog--footer li:first-child .emoji--item svg),
  :deep(.emoji-dialog--footer li:first-child .emoji--item i) {
    display: none !important;
  }
  :deep(.emoji-dialog--footer li:first-child .emoji--item)::before {
    content: '';
    display: inline-block;
    width: 16px;
    height: 16px;
    background-color: currentColor;
    -webkit-mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='12' cy='12' r='10'/%3E%3Cpolyline points='12 6 12 12 16 14'/%3E%3C/svg%3E");
    mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='12' cy='12' r='10'/%3E%3Cpolyline points='12 6 12 12 16 14'/%3E%3C/svg%3E");
    -webkit-mask-size: contain;
    mask-size: contain;
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
    -webkit-mask-position: center;
    mask-position: center;
  }
  :deep(.emoji-dialog--footer ul) {
    gap: 0 !important;
    justify-content: space-between;
    overflow: hidden !important;
    left: 0 !important;
    padding: 2px 0 !important;
  }
  :deep(.emoji-dialog--footer li) {
    flex: 1 1 0;
    position: relative;
    padding: 2px !important;
  }
  :deep(.emoji-dialog--footer li .emoji--item) {
    width: 100% !important;
    height: 34px !important;
    font-size: 22px;
    color: rgba(0, 0, 0, 0.5);
    transition:
      color 0.12s,
      opacity 0.12s;
    opacity: 0.65;
    filter: grayscale(0.15);
  }
  :deep(.emoji-dialog--footer li:hover .emoji--item) {
    opacity: 1;
    filter: none;
  }
  :deep(.emoji-dialog--footer li .active) {
    background: transparent !important;
    color: rgba(0, 0, 0, 0.95);
    font-weight: 700;
    opacity: 1 !important;
    filter: none !important;
  }
}

@media (max-width: 640px) {
  .aceleres-menu-container {
    min-width: 232px;
    max-width: min(272px, calc(100vw - 24px));
    border-radius: 12px;
  }

  .aceleres-menu-container :deep(.menu) {
    min-width: 232px;
    min-height: 44px;
    padding: 10px 14px;
  }

  .aceleres-reaction-pill :deep(.aceleres-emoji-btn) {
    width: 42px;
    height: 42px;
    font-size: 25px;
  }

  .aceleres-popup-root.is-emoji-picker {
    right: 8px !important;
    bottom: calc(8px + env(safe-area-inset-bottom)) !important;
    left: 8px !important;
    top: auto !important;
    width: auto;
    transform-origin: bottom center !important;
  }

  .aceleres-popup-root.is-emoji-picker .aceleres-wa-stack,
  .aceleres-popup-root.is-emoji-picker .aceleres-message-emoji-picker-card {
    width: 100%;
    max-width: none;
  }

  .aceleres-popup-root.is-emoji-picker .aceleres-message-emoji-picker-card {
    border-radius: 18px;
  }

  .aceleres-popup-root.is-emoji-picker
    .aceleres-message-emoji-picker-body
    :deep(.emoji-item) {
    height: min(42dvh, 320px) !important;
    max-height: min(42dvh, 320px) !important;
  }
}

@media (prefers-reduced-motion: reduce) {
  .aceleres-message-menu-enter-active,
  .aceleres-message-menu-leave-active,
  .aceleres-reaction-pill :deep(.aceleres-emoji-btn) {
    transition-duration: 0.01ms !important;
  }
}

/* Aceleres: modal de editar mensagem — estilo WhatsApp Web.
   X esquerda no header, textarea grande, footer com emoji + salvar. */
.aceleres-edit-popup-root {
  animation: aceleres-popup-in 0.12s cubic-bezier(0.16, 1, 0.3, 1) both;
}

.aceleres-edit-modal {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  max-width: none;
  max-height: 100%;
  background: var(--n-alpha-1, #fff);
  color: var(--s-900, #1f2933);
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow:
    0 12px 32px rgba(0, 0, 0, 0.22),
    0 2px 4px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  @apply dark:bg-n-solid-2 dark:border-n-alpha-2;
}

.aceleres-edit-modal__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--s-100, #e2e8f0);
}
.aceleres-edit-modal__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 999px;
  border: none;
  background: transparent;
  color: inherit;
  cursor: pointer;
  transition: background-color 0.15s ease;
}
.aceleres-edit-modal__close:hover {
  background: var(--s-100, #e2e8f0);
}
.aceleres-edit-modal__title {
  font-size: 15px;
  font-weight: 600;
  margin: 0;
}

.aceleres-edit-modal__body {
  padding: 16px;
  overflow: auto;
}
.aceleres-edit-modal__textarea {
  width: 100%;
  border: 1px solid var(--s-200, #cbd5e0);
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  line-height: 1.4;
  resize: vertical;
  min-height: 96px;
  background: var(--n-alpha-1, #fff);
  color: inherit;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}
.aceleres-edit-modal__textarea:focus {
  outline: none;
  border-color: #00a884;
  box-shadow: 0 0 0 2px rgba(0, 168, 132, 0.18);
}
.aceleres-edit-modal__hint {
  margin: 8px 2px 0;
  font-size: 12px;
  color: var(--s-500, #718096);
}

.aceleres-edit-modal__footer {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px 14px;
  border-top: 1px solid var(--s-100, #e2e8f0);
}
.aceleres-edit-modal__spacer {
  flex: 1;
}

.aceleres-edit-modal__emoji-wrap {
  position: relative;
}
.aceleres-edit-modal__emoji-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 999px;
  border: none;
  background: transparent;
  color: var(--s-500, #718096);
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;
}
.aceleres-edit-modal__emoji-btn:hover,
.aceleres-edit-modal__emoji-btn.is-active {
  background: var(--s-100, #e2e8f0);
  color: #00a884;
}
.aceleres-edit-modal__emoji-panel {
  /* Placeholder — não uso mais aqui, mantido só pra evitar quebrar CSS antigo. */
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
  z-index: 20;
}

/* Aceleres: card do picker de emoji do modal de editar. Compacto (300px),
   Teleportado pro body via .aceleres-edit-modal__emoji-panel-floating. Não
   herda do .aceleres-emoji-picker-card (que é 420px, tampava textarea). */
.aceleres-edit-modal__emoji-panel-floating {
  width: 300px;
  max-width: calc(100vw - 16px);
  max-height: 340px;
  background: #f7f8fa;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow:
    0 12px 32px rgba(0, 0, 0, 0.22),
    0 2px 4px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  @apply dark:bg-n-solid-2 dark:border-n-alpha-2;
}
.aceleres-edit-modal__emoji-picker-body {
  /* Deep overrides já vêm de .aceleres-emoji-picker-body (compartilhada com
     Reagir/ReplyBox). Aqui só garantimos que o grid tenha scroll interno
     dentro do max-height do card. */
  flex: 1 1 auto;
  min-height: 0;
}
.aceleres-edit-modal__emoji-picker-body :deep(.emoji-dialog) {
  height: 100% !important;
  max-height: 100% !important;
}
.aceleres-edit-modal__emoji-picker-body :deep(.emoji-dialog > .flex.flex-col) {
  height: 100% !important;
}
/* Em telas estreitas, deixa o picker um pouco menor pra sobrar respiro. */
@media (max-width: 480px) {
  .aceleres-edit-modal__emoji-panel-floating {
    width: 92vw;
    max-height: 60vh;
  }
}

.aceleres-edit-modal__cancel {
  padding: 8px 14px;
  border: none;
  background: transparent;
  color: var(--s-700, #4a5568);
  font-size: 13px;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.15s ease;
}
.aceleres-edit-modal__cancel:hover {
  background: var(--s-100, #e2e8f0);
}

.aceleres-edit-modal__send {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: none;
  background: #00a884;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  border-radius: 6px;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    opacity 0.15s ease;
}
.aceleres-edit-modal__send:hover:not(:disabled) {
  background: #069373;
}
.aceleres-edit-modal__send:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
</style>
